<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import Button from 'primevue/button';
import InfoCircleIcon from '@primeicons/vue/info-circle';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import { Typography } from '@apparatix/library/core';
import { useEndpoint } from '@apparatix/composables';


type Group = { psc_group: string };
type Code = { psc_group: string; psc_apx_code: string; selectionDesc: string };

type Props = {
  groups: Group[];
  codes: Code[];
  mediums: string[];
};

type ProductionChoicePayload = {
  group: string;
  code: string;
  operatorDescription: string;
};

const props = defineProps<Props>();

const selectedGroup = ref<string>('product');
const selectedCode = ref<string>();
const operatorDescription = ref<string>();

const isProductGroup = computed(() => selectedGroup.value === 'product');

const filteredCodes = computed(() =>
  props.codes.filter((c) => c.psc_group === selectedGroup.value),
);

watch(selectedGroup, () => {
  selectedCode.value = undefined;
});

const closeModal = () => {
  window.parent.document.dispatchEvent(
    new CustomEvent('closeAddPrintShopChoiceModalEvent'),
  );
};

const createProductionChoiceTranslation = async (payload: ProductionChoicePayload) => {
  await createEndpoint.getResults({ data: { choice: payload }});
  closeModal();
};

const createEndpoint = useEndpoint({
  type: 'POST',
  url: '/production/admin/add-choice',
  successField: 'id',
  toast: { showOnSuccess: false },
});

</script>

<template>
  <div class="flex flex-col gap-4 p-6">
    <Typography variant="section-heading">
      Adding New Printshop Choice Translation
    </Typography>
    <div>
      <div class="flex items-center gap-2">
        <Typography variant="form-label">
          Group
        </Typography>
        <InfoCircleIcon
          v-tooltip="'Category of choice translation. Product is for any item sold and is typically what you will select here. Shipping is for various shipping methods and the connected code should reflect that. Finish is only for Camel City and is used as a matching system for the final sent values.'"
          color="#6B7280"
          class="cursor-pointer"
        />
      </div>
      <Select
        v-model="selectedGroup"
        :options="props.groups"
        option-label="psc_group"
        option-value="psc_group"
        placeholder="Select a group"
        class="w-full"
      />
    </div>

    <div>
      <div class="flex items-center gap-2">
        <Typography variant="form-label">
          Code
        </Typography>
        <InfoCircleIcon
          v-tooltip="'Code controls what id we send to the printshop vendor on the order for either the product id or the shipping method id. When sending an order we map the Operator Description (below) to the related code in hal.printshop_choice via these settings. These must be set up for every product an end user wants to order from a printshop.'"
          color="#6B7280"
          class="cursor-pointer"
        />
      </div>
      <Select
        v-model="selectedCode"
        :options="filteredCodes"
        option-label="selectionDesc"
        option-value="psc_apx_code"
        placeholder="Search by code or description"
        class="w-full"
        filter
        :filter-fields="['psc_apx_code', 'selectionDesc']"
        filter-placeholder="Type to search..."
        show-clear
      />
    </div>

    <div>
      <div class="flex items-center gap-2">
        <Typography variant="form-label">
          {{ isProductGroup ? "Medium" : "Operator Description" }}
        </Typography>
        <InfoCircleIcon
          v-tooltip="'On Product for the group we match it to a medium from selections, for shipping it is a text description used by the operator to select on production ordering screens. For finish (only Camel City) it matches according to the sort order to find the result number in the json blob to send.'"
          color="#6B7280"
          class="cursor-pointer"
        />
      </div>

      <InputText
        v-if="!isProductGroup"
        v-model="operatorDescription"
        class="w-full"
        placeholder="Enter value..."
        :disabled="!selectedGroup"
      />

      <Select
        v-else
        v-model="operatorDescription"
        :options="mediums"
        class="w-full"
        placeholder="Select a medium"
        show-clear
        :disabled="!selectedGroup"
      />
    </div>

    <Button
      :disabled="!selectedGroup || !selectedCode || !operatorDescription"
      @click="createProductionChoiceTranslation({'group': selectedGroup, 'code': selectedCode, 'operatorDescription': operatorDescription})"
    >
      Save
    </Button>
  </div>
</template>
